import { useId, useState, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from '@zt/ui';
import { useI18n, useT } from '../../lib/i18n';

/**
 * Rapor grafikleri (F8-01). Tek seri sütun grafiği: ince sütun (en çok 24 px), üstü 4 px yuvarlatılmış, tabanı düz;
 * ince yatay kılavuz çizgileri; sütunun bütün dilimi üzerine gelme/odak alanıdır ve değeri ipucunda gösterir. Değerler
 * her zaman yanındaki tabloda da vardır (grafik yalnızca görsel özet). Tek seri olduğu için renk açıklaması yoktur;
 * başlık neyin çizildiğini söyler. Renk tasarım sisteminin vurgu rengi (pine); metinler metin renginde.
 */

const W = 640;
const H = 200;
const LEFT = 52;
const RIGHT = 8;
const TOP = 12;
const BOTTOM = 26;

/** 1-2-5 dizisinden temiz adım. */
function niceStep(raw: number): number {
  if (raw <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(raw));
  const f = raw / p;
  return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10) * p;
}

/** Üstü yuvarlatılmış, tabanı düz sütun. */
function columnPath(x: number, y: number, w: number, h: number): string {
  const r = Math.min(4, w / 2, h);
  return `M${x},${y + h}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h}Z`;
}

export interface ColumnPoint {
  key: string;
  /** Eksende ve ipucunda gösterilen dönem adı. */
  label: string;
  value: number;
}

export function ColumnChart({
  title,
  points,
  formatValue,
  formatTick,
  dimmed,
}: {
  title: string;
  points: ColumnPoint[];
  formatValue: (n: number) => string;
  /** Eksen değeri (kısaltılmış: 1 B, 12,5 B). */
  formatTick: (n: number) => string;
  /** Yeniden yüklenirken önceki çizim soluk kalır (yerleşim kaymaz). */
  dimmed?: boolean;
}) {
  const t = useT();
  const id = useId();
  const [active, setActive] = useState<number | null>(null);
  const max = Math.max(0, ...points.map((p) => p.value));
  const step = niceStep(max / 4);
  const top = Math.max(step, Math.ceil(max / step) * step);
  const ticks = Array.from({ length: Math.round(top / step) + 1 }, (_, i) => i * step);
  const plotW = W - LEFT - RIGHT;
  const plotH = H - TOP - BOTTOM;
  const slot = plotW / Math.max(points.length, 1);
  const bw = Math.max(2, Math.min(24, slot - 2));
  const yOf = (v: number) => TOP + plotH - (v / top) * plotH;
  // Eksen etiketleri seyrek: en çok 6 tane, ilk ve son her zaman.
  const every = Math.max(1, Math.ceil(points.length / 6));
  const onKey = (e: KeyboardEvent) => {
    if (!points.length) return;
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const d = e.key === 'ArrowRight' ? 1 : -1;
      setActive((a) => Math.min(points.length - 1, Math.max(0, (a ?? (d > 0 ? -1 : points.length)) + d)));
    } else if (e.key === 'Escape') setActive(null);
  };
  const a = active === null ? null : points[active];
  return (
    <figure className={cn('relative transition-opacity', dimmed && 'opacity-50')}>
      <figcaption className="mb-2 text-sm font-medium text-ink-2">{title}</figcaption>
      {max === 0 ? (
        <p className="rounded-md border border-line px-4 py-8 text-center text-sm text-ink-3">
          {t('admin.reports.chartEmpty')}
        </p>
      ) : (
        <div className="relative">
          <svg
            viewBox={`0 0 ${W} ${H}`}
            className="h-auto w-full touch-pan-y outline-none focus-visible:ring-2 focus-visible:ring-pine"
            role="img"
            aria-label={title}
            aria-describedby={a ? `${id}-tip` : undefined}
            tabIndex={0}
            onKeyDown={onKey}
            onBlur={() => setActive(null)}
            onPointerLeave={() => setActive(null)}
          >
            {ticks.map((v) => (
              <g key={v}>
                <line
                  x1={LEFT}
                  x2={W - RIGHT}
                  y1={yOf(v)}
                  y2={yOf(v)}
                  className="stroke-line"
                  strokeWidth={1}
                  vectorEffect="non-scaling-stroke"
                />
                <text
                  x={LEFT - 6}
                  y={yOf(v)}
                  textAnchor="end"
                  dominantBaseline="middle"
                  className="fill-ink-3 text-[11px] tabular-nums"
                >
                  {formatTick(v)}
                </text>
              </g>
            ))}
            {points.map((p, i) => {
              const x = LEFT + i * slot + (slot - bw) / 2;
              const h = top ? (p.value / top) * plotH : 0;
              return (
                <g key={p.key}>
                  {h > 0 && (
                    <path
                      d={columnPath(x, TOP + plotH - h, bw, h)}
                      className={i === active ? 'fill-pine-hover' : 'fill-pine'}
                    />
                  )}
                  {/* Dilimin tamamı üzerine gelme alanı (ince sütunu tutturmak zorunda kalınmaz). */}
                  <rect
                    x={LEFT + i * slot}
                    y={TOP}
                    width={slot}
                    height={plotH}
                    fill="transparent"
                    onPointerEnter={() => setActive(i)}
                    onPointerDown={() => setActive(i)}
                  />
                  {((i % every === 0 && points.length - 1 - i >= every) || i === points.length - 1) && (
                    <text
                      x={LEFT + i * slot + slot / 2}
                      y={H - 8}
                      textAnchor="middle"
                      className="fill-ink-3 text-[11px]"
                    >
                      {p.label}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>
          {a && active !== null && (
            <div
              id={`${id}-tip`}
              role="status"
              className="pointer-events-none absolute top-0 z-10 -translate-x-1/2 rounded-md border border-line bg-surface px-3 py-2 text-sm shadow-sm"
              style={{ left: `${((LEFT + active * slot + slot / 2) / W) * 100}%` }}
            >
              <div className="font-semibold text-ink tabular-nums">{formatValue(a.value)}</div>
              <div className="flex items-center gap-2 text-ink-3">
                <span aria-hidden className="inline-block h-0.5 w-3 bg-pine" />
                {a.label}
              </div>
            </div>
          )}
        </div>
      )}
    </figure>
  );
}

/** Değişim oranı (baz puan); önceki sıfırsa null. */
export function deltaBps(cur: number, prev: number): number | null {
  if (prev === 0) return null;
  return Math.round(((cur - prev) / Math.abs(prev)) * 10_000);
}

/**
 * Özet kutucuğu: ad, değer, önceki döneme göre değişim (yön sözcük ve okla yazılır; renk tek başına anlam taşımaz).
 */
export function StatTile({
  label,
  value,
  current,
  previous,
  previousLabel,
  comparedTo,
  help,
}: {
  label: string;
  value: string;
  current: number;
  previous: number;
  previousLabel: string;
  /** Karşılaştırılan dönemin adı (ör. "Dün"); boşsa "Önceki dönem". */
  comparedTo?: string;
  help?: ReactNode;
}) {
  const t = useT();
  const { formatNumber } = useI18n();
  const d = deltaBps(current, previous);
  const text =
    d === null
      ? current === 0
        ? t('admin.reports.deltaFlat')
        : t('admin.reports.deltaNew')
      : d === 0
        ? t('admin.reports.deltaFlat')
        : t(d > 0 ? 'admin.reports.deltaUp' : 'admin.reports.deltaDown', {
            pct: formatNumber(Math.abs(d) / 100, Math.abs(d) < 1000 ? 1 : 0),
          });
  const arrow = d === null || d === 0 ? '' : d > 0 ? '↑ ' : '↓ ';
  return (
    <div className="rounded-md border border-line bg-surface p-4">
      <div className="flex h-6 items-center gap-1 text-sm text-ink-3">
        {label}
        {help}
      </div>
      <div className="mt-1 text-2xl font-semibold text-ink">{value}</div>
      <div className="mt-1 text-xs text-ink-2">
        <span aria-hidden>{arrow}</span>
        {text}
      </div>
      <div className="text-xs text-ink-3">
        {comparedTo
          ? t('admin.reports.vsNamed', { name: comparedTo, value: previousLabel })
          : t('admin.reports.vsPrevious', { value: previousLabel })}
      </div>
    </div>
  );
}
